<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { ref, watch, onMounted, useRouter, onUnmounted, nextTick } from "#imports";
import { ClientOnly } from "#components";
import { useAuthState } from "~/composables/useAuthState";
import PointsHistoryPopup from "~/components/PointsHistoryPopup.vue";


const { t } = useI18n();
const showSettings = ref(false);
const userMenuTrigger = ref<HTMLButtonElement | null>(null);

// 使用新的认证状态composable
const {
  user,
  isAuthenticated,
  isLoggingIn,
  handleSignIn,
  totalPoints,
  userAccount,
  isLoadingAccount,
  isRefreshing,
  handleLogout,
} = useAuthState();

// 积分历史弹窗
const showPointsHistoryPopup = ref(false);

// 处理用户名过长中间省略号
const truncateMiddle = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  const partLength = Math.floor((maxLength - 3) / 2);
  return text.substr(0, partLength) + "..." + text.substr(-partLength);
};

// 用户信息
const userInfo = ref<any>({
  avatar: "", // 默认头像
  name: "",
  email: "", // 添加邮箱字段
});

// 更新用户信息
const updateUserInfo = () => {
  if (user.value) {
    userInfo.value.name = user.value.firstName || t("user.role");
    userInfo.value.avatar =
      user.value.imageUrl || "/assets/svg/ic-sharp-person.svg";
    userInfo.value.email =
      user.value.emailAddresses?.[0]?.emailAddress || t("user.noEmail");
  }
};

// 监听用户信息变化，仅处理UI更新
watch(
  user,
  () => {
    if (user.value) {
      updateUserInfo();
    }
  },
  { deep: true }
);

// 监听认证状态变化，仅处理UI更新
watch(isAuthenticated, (newValue: any) => {
  if (newValue) {
    updateUserInfo();
  } else {
    showSettings.value = false;
  }
});

const navItems = [
  { icon: "i-lucide-home", key: "nav.home", route: "/" },
  {
    icon: "i-lucide-edit",
    key: "nav.create",
    route: "/create",
    children: [
      {
        icon: "i-lucide-music",
        key: "nav.createMusic",
        route: "/create"
      },
      {
        icon: "i-lucide-clapperboard",
        key: "nav.clips",
        route: "/clips"
      },
      {
        icon: "i-lucide-video",
        key: "nav.mvGenerator",
        route: "/mv-generator"
      },
      {
        icon: "i-lucide-mic",
        key: "nav.rapLyric",
        route: "/rap-lyrics-generator"
      },
      {
        icon: "i-lucide-file-text",
        key: "nav.lyricGenerator",
        route: "/lyrics-generator"
      },
      {
        icon: "i-lucide-image",
        key: "nav.albumCover",
        route: "/album-cover-generator"
      },
      // MV 生成功能暂不支持，隐藏入口
      // {
      //   icon: "i-lucide-video",
      //   key: "nav.mvGenerator",
      //   route: "/mv-generator"
      // }
    ]
  },
  {
    icon: "i-lucide-wrench",
    key: "nav.tools",
    route: "/rap-name-generator",
    children: [
      {
        icon: "i-lucide-user",
        key: "nav.rapnameGenerator",
        route: "/rap-name-generator"
      },
      {
        icon: "i-lucide-scissors",
        key: "nav.vocalSeparation",
        route: "/vocal-separation"
      },
    ]
  },
  { icon: "i-lucide-library", key: "nav.library", route: "/library" },
  {
    icon: "i-lucide-crown",
    key: "nav.pricing",
    route: "/pricing",
  },
] as const;

const router = useRouter();
const isMobile = ref(false);
watch(isMobile, async mobile => {
  if (!mobile || !showSettings.value) return;
  showSettings.value = false;
  await nextTick();
  document.querySelector<HTMLButtonElement>('.mobile-header button')?.focus();
});
watch(() => user.value?.id, () => { showSettings.value = false; });
watch(() => router.currentRoute.value.fullPath, () => { showSettings.value = false; });
const showCreateMenu = ref(true);
const showToolsMenu = ref(false);

// 检查是否移动端
const checkMobile = () => {
  if (typeof window !== 'undefined') {
    isMobile.value = window.innerWidth < 1024;
  }
};

// 检查当前路由是否为创作相关路由
const checkIfCreateRoute = () => {
  const path = router.currentRoute.value.path;
  const createRoutes = ['/create', '/clips', '/lyrics-generator', '/rap-lyrics-generator', '/album-cover-generator', '/mv-generator'];
  const toolsRoutes = ['/rap-name-generator', '/vocal-separation'];

  // 如果当前路由是创作相关路由，则显示创作子菜单
  if (createRoutes.includes(path) || path.startsWith('/clips/')) {
    showCreateMenu.value = true;
  }
  if (toolsRoutes.includes(path)) {
    showToolsMenu.value = true;
  }
};

// 监听页面大小和路由变化
onMounted(() => {
  try {
    // 检测移动端
    if (typeof window !== 'undefined') {
      checkMobile();
      window.addEventListener('resize', checkMobile);
    }
    
    // 初始检查当前路由是否创作相关
    checkIfCreateRoute();
  } catch (error) {
    console.error('AppNavigation挂载失败:', error);
  }
});

onUnmounted(() => {
  try {
    console.log('AppNavigation: 组件开始卸载');
    
    // 移除全局事件监听器
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', checkMobile);
    }
    
    // 确保菜单和弹窗都已关闭
    showSettings.value = false;
    showCreateMenu.value = false;
    showToolsMenu.value = false;
    showPointsHistoryPopup.value = false;
    
    console.log('AppNavigation: 组件卸载完成');
  } catch (error) {
    console.error('AppNavigation卸载失败:', error);
  }
});

// 处理导航点击
const handleNavClick = (item: any, event: MouseEvent) => {
  event.stopPropagation(); // 阻止事件冒泡
  if (item.children) {
    if (isMobile.value) {
      // 移动端直接跳转到对应页面
      nextTick(() => {
        if (item.key === 'nav.create') {
          router.push('/m-create');
        } else {
          router.push(item.route);
        }
      });
    } else {
      // 桌面端切换子菜单
      if (item.key === 'nav.create') {
        showCreateMenu.value = !showCreateMenu.value;
      } else if (item.key === 'nav.tools') {
        showToolsMenu.value = !showToolsMenu.value;
      }
    }
  } else {
    nextTick(() => {
      router.push(item.route);
    });
  }
};

const toggleSettings = () => {
  showSettings.value = !showSettings.value;
};
const closeSettings = () => { showSettings.value = false; };

// 处理本地登出UI
const handleLocalLogout = async () => {
  await handleLogout();
  showSettings.value = false;
};

// 打开积分历史弹窗
const isNavRouteActive = (targetRoute: string) => {
  const path = router.currentRoute.value.path;
  if (targetRoute === '/clips') {
    return path === '/clips' || path.startsWith('/clips/');
  }
  return path === targetRoute;
};

const openPointsHistoryPopup = () => {
  showPointsHistoryPopup.value = true;
  showSettings.value = false; // 关闭设置菜单
};
</script>

<template>
  <nav class="nav-container" :inert="showSettings">
    <div
      class="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-purple-500/20 to-transparent hidden lg:block"
    ></div>
    <div class="nav-content">
      <!-- 固定的logo部分 -->
      <div class="nav-header-fixed">
        <div class="nav-header">
          <div class="logo-container">
            <img
              src="/images/logo.png"
              alt="MusicMint AI Music Generator Logo"
              class="w-6 h-6"
              width="24"
              height="24"
            />
            <span class="logo-text">MusicMint</span>
          </div>
        </div>
      </div>
      
      <!-- 可滚动的内容部分 -->
      <div class="nav-scrollable-content">
        <div class="nav-top">
          <div class="nav-items">
            <div
              v-for="item in navItems"
              :key="item.key"
              class="cursor-pointer relative nav-item"
              :class="{ active: isNavRouteActive(item.route) }"
            >
              <component :is="item.children ? 'button' : 'a'"
                :href="item.children ? undefined : item.route" :type="item.children ? 'button' : undefined"
                :aria-expanded="item.children ? (item.key === 'nav.create' ? showCreateMenu : showToolsMenu) : undefined"
                :aria-controls="item.children ? `desktop-${item.key}` : undefined"
                class="flex items-center w-full"
                @click.prevent="(e) => handleNavClick(item, e)"
              >
                <div :class="[item.icon, 'nav-icon']" />
                <span class="nav-label">{{ t(item.key) }}</span>
                <!-- 只在有子菜单的项显示箭头，且只在PC端显示 -->
                <div v-if="item.children" class="ml-auto hidden lg:block">
                  <div
                    :class="[
                      'i-lucide-chevron-down transition-transform duration-200',
                      { 'rotate-180': item.key === 'nav.create' ? showCreateMenu : showToolsMenu }
                    ]"
                  />
                </div>
              </component>

              <!-- 子菜单 -->
              <div
                v-if="item.children && (item.key === 'nav.create' ? showCreateMenu : showToolsMenu) && !isMobile"
                class="create-menu"
                :id="`desktop-${item.key}`"
              >
                <a
                  v-for="(child, index) in item.children"
                  :key="child.key"
                  :href="child.route"
                  class="create-menu-item"
                  :class="[
                    { active: isNavRouteActive(child.route) },
                    { 'mt-1': index === 0 }
                  ]"
                  @click.stop.prevent="router.push(child.route)"
                >
                  <div :class="[child.icon, 'mr-2 text-lg']" />
                  <span>{{ t(child.key) }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="user-container">
          <UserSettingsPopup
            v-if="showSettings"
            :anchor="userMenuTrigger"
            @close="closeSettings"
            @logout="handleLocalLogout"
          />

          <!-- 页脚链接 -->
          <div class="footer-links">
            <!-- 语言切换 -->
            <div class="sidebar-language-switcher">
              <LanguageSwitcher />
            </div>
            <a
              href="/privacy.html"
              target="_blank"
              class="footer-link text-sm py-2 lg:px-4 lg:py-2 flex items-center justify-start"
            >
              <div class="i-lucide-shield mr-2"></div>
              {{ t("footer.privacyPolicy") }}
            </a>
            <a
              href="/terms.html"
              target="_blank"
              class="footer-link text-sm py-2 lg:px-4 lg:py-2 flex items-center justify-start"
            >
              <div class="i-lucide-file-check mr-2"></div>
              {{ t("footer.termsOfService") }}
            </a>
            <a
              href="/data-deletion.html"
              target="_blank"
              class="footer-link text-sm py-2 lg:px-4 lg:py-2 flex items-center justify-start"
            >
              <div class="i-lucide-user-x mr-2"></div>
              {{ t("footer.dataDeletion") }}
            </a>
            <NuxtLink
              to="/about"
              class="footer-link text-sm py-2 lg:px-4 lg:py-2 flex items-center justify-start"
            >
              <div class="i-lucide-info mr-2"></div>
              {{ t("footer.aboutUs") }}
            </NuxtLink>
          </div>
          <div class="min-h-20 flex-shrink-0 flex items-end justify-start">
            <ClientOnly>
              <div class="text-center w-full" v-if="!isAuthenticated">
                <div class="login-modal text-center px-5" >
                  <button
                    @click="handleSignIn"
                    class="login-button m-auto w-full text-center"
                    :disabled="isLoggingIn"
                  >
                    {{ t("user.login") }}
                    <span v-if="isLoggingIn" class="loading-spinner"></span>
                  </button>
                </div>
              </div>

              <!-- 已登录用户信息 -->
              <div class="box-border w-full" v-if="isAuthenticated">
                <button ref="userMenuTrigger" type="button" :aria-label="userInfo.name" aria-haspopup="dialog" :aria-expanded="showSettings"
                  class="pl-4 user-info w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-300" @click="toggleSettings">
                  <img
                    v-if="userInfo.avatar"
                    :src="userInfo.avatar"
                    :alt="userInfo.name"
                    class="user-avatar"
                  />
                  <span class="user-details">
                    <span class="user-name">{{ userInfo.name }}</span>
                    <span class="user-email">{{
                      truncateMiddle(userInfo.email, 17)
                    }}</span>
                  </span>
                </button>
                <!-- 用户积分信息 -->
                <button type="button" :aria-label="t('user.pointsHistory')" :aria-busy="isLoadingAccount"
                  class="pl-4 mt-2 min-h-[44px] text-sm ml-1 flex items-center cursor-pointer text-white hover:text-blue-500 dark:hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-300"
                  @click="openPointsHistoryPopup"
                >
                  <span class="flex items-center">
                    {{ t("user.points") }}: 
                    <span :class="['ml-1', { 'animate-pulse': isRefreshing }]">
                      {{ userAccount ? totalPoints : '—' }}
                    </span>
                  </span>
                  <span
                    class="cursor-pointer pl-4 underline text-blue-500 dark:text-blue-400"
                    >{{ t("common.view") }}</span
                  >
                </button>
              </div>
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>
  </nav>

  <!-- 积分历史弹窗 -->
  <PointsHistoryPopup v-model:visible="showPointsHistoryPopup" />


</template>
<style>
.cl-modalBackdrop {
  align-items: center !important;
}
</style>
<style scoped>
.nav-container {
  @apply fixed shadow-lg z-20;
  /* PC端样式 */
  @apply lg:!w-[250px] lg:left-0 lg:top-0 lg:bottom-0 lg:!h-screen;
  /* 移动端样式 - 隐藏移动端的主导航容器 */
  @apply fixed bottom-0 left-0 w-full h-[3.5rem] bg-white dark:bg-gray-900 hidden lg:block;
}

.nav-content {
  @apply w-full h-screen min-h-0 flex flex-col relative z-10;
  /* PC端样式调整 */
  @apply lg:py-5 lg:px-6;
  /* 移动端样式调整 */
  @apply p-2;
}

/* 固定的头部Logo区域 */
.nav-header-fixed {
  @apply w-full lg:block hidden flex-shrink-0;
  @apply lg:mb-4;
}

/* 可滚动内容区域 */
.nav-scrollable-content {
  @apply flex flex-col justify-between flex-1 min-h-0;
  /* 矮窗口和展开菜单共用滚动区，滚动条提示屏幕下方仍有入口。 */
  @apply lg:overflow-y-auto;
  scrollbar-width: thin;
  scrollbar-color: #475569 transparent;
  overscroll-behavior: contain;
  @apply lg:mt-0 lg:mb-0;
}

.nav-top {
  @apply flex flex-col flex-shrink-0 gap-4;
}

.nav-header {
  @apply w-full;
}

.logo-container {
  @apply flex items-center gap-3 lg:pl-4;
}

.logo-circle {
  @apply w-8 h-8 rounded-full bg-blue-500 text-white;
  @apply flex items-center justify-center text-xl font-bold;
}

.logo-text {
  @apply text-lg font-semibold text-gray-800 dark:text-white;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  font-weight: 600;
}

.nav-items {
  @apply flex;
  /* PC端垂直排列 */
  @apply lg:!flex-col lg:gap-1;
  /* 移动端水平排列 - 完全隐藏 */
  @apply hidden lg:flex;
}

.nav-item {
  @apply flex flex-col transition-all duration-200 relative;
  /* 移动端样式：垂直排列 */
  @apply flex-col items-center gap-1 p-2;
  /* PC端样式：水平排列 */
  @apply lg:flex-col lg:items-start lg:px-0 lg:py-0 lg:w-full;
  @apply text-gray-500 dark:text-gray-400;
  display: flex;
}

.nav-item > a,
.nav-item > button {
  /* 内层链接继承颜色 */
  @apply text-inherit;
  /* 移除 a 标签默认样式 */
  text-decoration: none;
  /* PC端样式 */
  @apply lg:px-4 lg:py-2.5 lg:w-full;
}

.nav-item.active {
  @apply text-blue-600 dark:text-blue-400;
}

.nav-item.active::before {
  content: "";
  @apply absolute left-0 w-1 bg-blue-600 dark:bg-blue-400 rounded-r-md;
  @apply hidden lg:block;
  /* 确保选中条高度固定 */
  height: 22px;
  top: 9px; /* 固定到文字位置 */
}

.nav-item:hover {
  @apply text-gray-800 dark:text-gray-200;
}

.nav-icon {
  @apply text-xl transition-colors duration-200;
  /* PC端样式 */
  @apply lg:mr-3;
}

.nav-label {
  @apply hidden font-medium whitespace-nowrap text-xs transition-colors duration-200;
  /* PC端样式 */
  @apply lg:text-sm lg:block;
}

.user-container {
  @apply relative flex-shrink-0;
  /* 移动端隐藏用户信息 */
  @apply lg:block hidden;
  /* 增加底部距离 */
  @apply lg:mb-6;
}

.login-button {
  @apply bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700;
  @apply text-white font-medium px-6 py-2.5 rounded-lg shadow-md;
  @apply transition-all duration-300 transform hover:scale-105;
  @apply flex items-center justify-center gap-2;
  @apply disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100;
  /* 确保只在大屏幕上显示背景 */
  @apply lg:bg-gradient-to-r;
}

.logout-button {
  @apply bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md mt-2;
}

.token-info {
  @apply bg-gray-100 dark:bg-gray-800 p-3 rounded-md mt-2 text-xs;
  @apply max-w-[180px] overflow-hidden text-ellipsis;
}

.user-info {
  @apply flex items-center gap-3 rounded-xl;
  @apply py-4 mt-0 mb-2;
  @apply hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer;
}

.user-avatar {
  @apply w-10 h-10 rounded-full ring-2 ring-blue-100 dark:ring-blue-900;
}

.user-details {
  @apply flex flex-col justify-center;
}

.user-name {
  @apply text-sm font-medium text-gray-800 dark:text-gray-200;
}

.user-email {
  @apply text-xs text-gray-500 dark:text-gray-400;
  /* 调整邮箱显示，保证省略号在中间 */
  @apply truncate max-w-[120px] text-ellipsis;
}

.user-role {
  @apply text-xs text-gray-500 dark:text-gray-400;
}

/* 语言选择器样式 */
.language-selector {
  @apply flex items-center gap-2 p-4 rounded-xl cursor-pointer;
  @apply hover:bg-gray-100 dark:hover:bg-gray-800;
  @apply text-sm text-gray-700 dark:text-gray-300;
  @apply mb-2;
}

.language-menu {
  @apply absolute bottom-full left-0 right-0 mb-2;
  @apply bg-white dark:bg-gray-800 rounded-xl shadow-lg;
  @apply p-4 z-30;
}

.language-menu-header {
  @apply flex justify-between items-center mb-3;
  @apply text-sm font-medium text-gray-700 dark:text-gray-300;
}

.close-button {
  @apply text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200;
}

.language-options {
  @apply flex flex-col gap-2;
}

.language-option {
  @apply px-3 py-2 rounded-lg text-left;
  @apply text-sm text-gray-700 dark:text-gray-300;
  @apply hover:bg-gray-100 dark:hover:bg-gray-700;
}

.language-option.active {
  @apply bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400;
}

.truncate {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}

/* 添加加载动画 */
.loading-spinner {
  @apply inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2;
}

/* 页脚链接样式 */
.footer-links {
  @apply flex flex-col gap-0.5 mb-3 px-2;
  @apply text-gray-500 dark:text-gray-400;
}

.footer-link {
  @apply hover:text-gray-700 dark:hover:text-gray-300 transition-colors;
}

.sidebar-language-switcher :deep(button) {
  @apply w-full justify-start px-4 py-2;
}

/* 添加刷新动画 */
.animate-pulse {
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.create-menu {
  @apply w-full;
  @apply overflow-hidden;
  animation: slideDown 0.2s ease-in-out;
}

.create-menu-item {
  @apply px-4 py-2.5 cursor-pointer flex items-center;
  @apply text-sm text-gray-500 dark:text-gray-400;
  @apply pl-6; /* 添加左侧缩进 */
  /* 移除 a 标签默认样式 */
  text-decoration: none;
  display: flex;
}

.create-menu-item:hover {
  @apply text-gray-800 dark:text-gray-200;
}

/* 添加二级菜单选中样式 */
.create-menu-item.active {
  @apply text-blue-600 dark:text-blue-400;
  @apply bg-transparent;
}

/* 移除二级菜单的左侧边框 */
.create-menu-item.active::before {
  display: none;
}

/* 当创作菜单展开时，调整选中条的位置 */
.nav-item:has(.create-menu.active)::before {
  top: 9px; /* 保持与普通菜单项一致的位置 */
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 200px;
  }
}

/* 确保移动端不显示下拉菜单 */
@media (max-width: 1023px) {
  .create-menu {
    display: none;
  }
}

/* 移除之前的边框相关样式 */
.nav-item:has(.create-menu) {
  @apply border-b-0;
}

</style> 
